/**
 * Batch Document Processing Page
 * ===============================
 * Admin page for processing multiple documents at once using Textract OCR.
 * 
 * Features:
 * - List available S3 documents
 * - Select multiple documents for batch processing
 * - Track processing progress
 * - View extracted transactions
 */

import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Upload, 
  Play, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Loader2,
  FolderOpen,
  Search
} from 'lucide-react';
import api from '../utils/api';

interface S3Document {
  s3_key: string;
  filename: string;
  size_bytes: number;
  last_modified: string;
}

interface BatchJob {
  batch_id: string;
  status: string;
  total_documents: number;
  processed_documents: number;
  failed_documents: number;
  documents: Array<{
    s3_key: string;
    status: string;
    transaction_count?: number;
    error?: string;
  }>;
  created_at: string;
  completed_at?: string;
}

export default function BatchProcessingPage() {
  const [documents, setDocuments] = useState<S3Document[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentJob, setCurrentJob] = useState<BatchJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [prefix, setPrefix] = useState('uploads/');
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, [prefix]);

  // Poll for job status when processing
  useEffect(() => {
    if (!currentJob || currentJob.status === 'completed' || currentJob.status === 'failed') {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const status = await api.get(`/api/v1/batch-process/status/${currentJob.batch_id}`);
        setCurrentJob(status);
        
        if (status.status === 'completed' || status.status === 'failed') {
          setProcessing(false);
        }
      } catch (err) {
        console.error('Error polling job status:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [currentJob?.batch_id, currentJob?.status]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/api/v1/batch-process/list-s3-documents?prefix=${encodeURIComponent(prefix)}&max_keys=100`);
      setDocuments(response.documents || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const toggleDocSelection = (s3Key: string) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(s3Key)) {
      newSelected.delete(s3Key);
    } else {
      newSelected.add(s3Key);
    }
    setSelectedDocs(newSelected);
  };

  const selectAll = () => {
    const filtered = filteredDocuments;
    if (selectedDocs.size === filtered.length) {
      setSelectedDocs(new Set());
    } else {
      setSelectedDocs(new Set(filtered.map(d => d.s3_key)));
    }
  };

  const startBatchProcessing = async () => {
    if (selectedDocs.size === 0) return;
    
    setProcessing(true);
    setError(null);
    
    try {
      const response = await api.post('/api/v1/batch-process/start', {
        s3_keys: Array.from(selectedDocs)
      });
      
      setCurrentJob({
        batch_id: response.batch_id,
        status: response.status,
        total_documents: response.total_documents,
        processed_documents: 0,
        failed_documents: 0,
        documents: Array.from(selectedDocs).map(key => ({ s3_key: key, status: 'pending' })),
        created_at: new Date().toISOString()
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start batch processing');
      setProcessing(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'failed':
        return <XCircle className="text-red-500" size={18} />;
      case 'processing':
        return <Loader2 className="text-blue-500 animate-spin" size={18} />;
      default:
        return <Clock className="text-gray-400" size={18} />;
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.filename.toLowerCase().includes(searchFilter.toLowerCase()) ||
    doc.s3_key.toLowerCase().includes(searchFilter.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Upload className="text-blue-600" />
            Batch Document Processing
          </h1>
          <p className="text-gray-600 mt-1">
            Process multiple documents with Textract OCR and AI-powered transaction extraction
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {/* Current Job Status */}
        {currentJob && (
          <div className="mb-6 bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                {getStatusIcon(currentJob.status)}
                Batch Job: {currentJob.batch_id.slice(0, 8)}...
              </h3>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                currentJob.status === 'completed' ? 'bg-green-100 text-green-700' :
                currentJob.status === 'failed' ? 'bg-red-100 text-red-700' :
                currentJob.status === 'processing' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {currentJob.status.toUpperCase()}
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Progress</span>
                <span>
                  {currentJob.processed_documents + currentJob.failed_documents} / {currentJob.total_documents}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${((currentJob.processed_documents + currentJob.failed_documents) / currentJob.total_documents) * 100}%` 
                  }}
                />
              </div>
            </div>

            {/* Document Status List */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {currentJob.documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(doc.status)}
                    <span className="text-sm text-gray-700 truncate max-w-md">
                      {doc.s3_key.split('/').pop()}
                    </span>
                  </div>
                  <div className="text-sm">
                    {doc.transaction_count !== undefined && (
                      <span className="text-green-600">{doc.transaction_count} transactions</span>
                    )}
                    {doc.error && (
                      <span className="text-red-600" title={doc.error}>Error</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Document Browser */}
        <div className="bg-white rounded-lg shadow">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <FolderOpen size={18} className="text-gray-400" />
                <input
                  type="text"
                  value={prefix}
                  onChange={(e) => setPrefix(e.target.value)}
                  placeholder="S3 prefix..."
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <Search size={18} className="text-gray-400" />
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder="Filter documents..."
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <button
                onClick={fetchDocuments}
                disabled={loading}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                title="Refresh"
              >
                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {selectedDocs.size} of {filteredDocuments.length} selected
              </span>
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:underline"
              >
                {selectedDocs.size === filteredDocuments.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={startBatchProcessing}
                disabled={selectedDocs.size === 0 || processing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Play size={18} />
                )}
                Process Selected ({selectedDocs.size})
              </button>
            </div>
          </div>

          {/* Document List */}
          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
                <p className="text-gray-500">Loading documents...</p>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>No documents found in this location</p>
              </div>
            ) : (
              filteredDocuments.map((doc) => (
                <div
                  key={doc.s3_key}
                  onClick={() => toggleDocSelection(doc.s3_key)}
                  className={`p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 ${
                    selectedDocs.has(doc.s3_key) ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedDocs.has(doc.s3_key)}
                      onChange={() => toggleDocSelection(doc.s3_key)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300"
                    />
                    <FileText className="text-gray-400" size={20} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.filename}</p>
                      <p className="text-xs text-gray-500 truncate max-w-md">{doc.s3_key}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">{formatBytes(doc.size_bytes)}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(doc.last_modified).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
